/**
 * Shared pure logic for TemplateSelector/SegmentSelector (redesign-panel-hubspot-mailgun,
 * PR 9, task 9.9 — REFACTOR). Both selectors independently implemented the SAME two
 * behaviors — normalized-substring name filtering over an already-fetched bounded list, and
 * detecting whether the currently assigned id falls outside that bounded window — with
 * identical semantics. Extracted here rather than into a single shared selector COMPONENT,
 * because the rendered markup genuinely differs per consumer (templates show
 * producto/version/inactive-disabled options; segments show criteria/member-count) — task
 * 9.9: "share... primitives only where semantics match", never a forced abstraction.
 */

/** Case-insensitive substring match on `nameOf(option)`, trimming the filter first. Empty filter returns every option unchanged. */
export function filterOptionsByName<T>(options: readonly T[], filter: string, nameOf: (option: T) => string): T[] {
  const normalized = filter.trim().toLowerCase();
  if (!normalized) return [...options];
  return options.filter((option) => nameOf(option).toLowerCase().includes(normalized));
}

/**
 * True when `value` is a non-empty id that does not appear among `options` — i.e. the
 * currently assigned template/segment falls outside the bounded (`size<=50`) options window
 * fetched for this editor. `options` may be `undefined` while the query has not resolved yet.
 */
export function isSelectedOutsideWindow<T>(
  value: string,
  options: readonly T[] | undefined,
  idOf: (option: T) => string,
): boolean {
  if (value === "" || !options) return false;
  return !options.some((option) => idOf(option) === value);
}
